import React, { useState, useEffect, useMemo } from 'react';
import { useVocab } from '../hooks/useVocab';
import { useWordTimes } from '../hooks/useWordTimes';
import { answer, previewLabel, isDue } from '../utils/anki';
import { todayStr } from '../utils/sm2';
import { API_BASE, MEDIA_BASE, MEDIA_V } from '../api';
import { loadStoriesIn, loadWordDialects } from '../library';
import { cleanWord, reviewKey } from '../utils/wordText';
import { playWord, stopAll } from '../utils/audio';
import { useSwipe } from '../hooks/useSwipe';
import { STATIC } from '../staticMode';
import { lookupGloss } from '../dict';
import { shortGloss } from '../utils/glossText';

const encPath = (a) => { const i = a.indexOf('/'); return i < 0 ? encodeURIComponent(a) : a.slice(0, i + 1) + encodeURIComponent(a.slice(i + 1)); };

// edge-trim everything that isn't a letter/digit/apostrophe/^ — apostrophes and
// ^ are letters in these orthographies (ka’a’, pitu’, pala^), not punctuation
const cl = (t) => (t || '').replace(/^[^\p{L}\p{N}'’ʼ‘^]+|[^\p{L}\p{N}'’ʼ‘^]+$/gu, '').toLowerCase();

// Map a sentence-level word index to the leaf (segment) holding it + index within the leaf.
function leafAt(s, wi) {
  const leaves = (s.segments && s.segments.length) ? s.segments : [s];
  let off = 0;
  for (const leaf of leaves) {
    const n = (leaf.words || []).length;
    if (wi < off + n) return { leaf, wi: wi - off };
    off += n;
  }
  return null;
}

// Resolve a saved mark-location within a story. loc.itemId is whatever unit the word
// was tapped in: a segment id (片), a sentence id (句/文), or `para_N` (段 — where
// loc.wi indexes the paragraph's flattened words).
function resolveLoc(st, loc) {
  const paras = st.paragraphs || [];
  const pm = /^para_(\d+)$/.exec(loc.itemId || '');
  if (pm) {
    let wi = loc.wi;
    for (const s of (paras[+pm[1]] || {}).sentences || []) {
      const n = (s.words || []).length;
      if (wi < n) { const h = leafAt(s, wi); return h && { s, ...h }; }
      wi -= n;
    }
    return null;
  }
  for (const p of paras) {
    for (const s of p.sentences || []) {
      if (s.id === loc.itemId) { const h = leafAt(s, loc.wi); return h && { s, ...h }; }
      for (const leaf of s.segments || []) {
        if (leaf.id === loc.itemId) return (leaf.words || [])[loc.wi] ? { s, leaf, wi: loc.wi } : null;
      }
    }
  }
  return null;
}

// The example for a review card: the SEGMENT (leaf unit) holding the word — example +
// audio. Prefers WHERE the word was marked 學習中 (card.loc), so 跳到故事 returns there;
// falls back to the first occurrence (pre-loc cards, or the text changed under the loc).
// Searching at the segment level keeps the timing key (`code:segmentId:wordIdx`) aligned
// with what the reader reads.
// `stories` is now passed in — the caller fetches the dialect's books first (they
// are no longer bundled), so this stays a pure search over what it was given.
function findExample(stories, word, loc) {
  const k = cl(word);
  if (loc && loc.code) {
    const st = stories.find(t => (t.code || t.id) === loc.code);
    const hit = st && resolveLoc(st, loc);
    if (hit && (hit.leaf.src || hit.leaf.audio) && cl((hit.leaf.words[hit.wi] || {}).text) === k) {
      return { st, code: loc.code, leafId: hit.leaf.id, leaf: hit.leaf, s: hit.s, wi: hit.wi, word: hit.leaf.words[hit.wi] };
    }
  }
  for (const st of stories) {
    const code = st.code || st.id;
    for (const p of st.paragraphs || []) {
      for (const s of p.sentences || []) {
        const leaves = (s.segments && s.segments.length) ? s.segments : [s];
        for (const leaf of leaves) {
          const ws = leaf.words || [];
          const wi = ws.findIndex(w => cl(w.text) === k);
          if (wi >= 0 && (leaf.src || leaf.audio)) return { st, code, leafId: leaf.id, leaf, s, wi, word: ws[wi] };
        }
      }
    }
  }
  return null;
}

const API = API_BASE;

// word -> [dialect]. This used to be an IIFE that walked every word of all sixteen
// stories at module load — which is to say, the bundle had to contain all sixteen.
// It is baked by scripts/gen_library.mjs now and fetched when a review session
// opens (~82 kB raw, ~21 kB gzipped).
//
// Keyed and looked up through the SAME reviewKey, which is the fix for a mismatch
// that used to live here: the table was keyed with cleanWord and read with a local
// stripPunct, so every card on ka’a’ or hahila: — the words whose apostrophes and
// colons are letters — missed, and its dialect could not be inferred.
function inDialect(wd, word, card, dia) {
  if (card.dialect) return card.dialect === dia;
  const set = wd && wd[reviewKey(word)];
  return set ? set.includes(dia) : false;
}

function Header({ onBack, count }) {
  return (
    <header className="ph-header">
      <button className="ph-back" onClick={onBack}>‹ 書庫</button>
      <span className="ph-htitle">複習</span>
      <span className="ph-hright">{count || ''}</span>
    </header>
  );
}

const GRADES = [
  { key: 'again', label: '忘記', cls: 'srs-act-again' },
  { key: 'hard', label: '困難', cls: 'srs-act-hard' },
  { key: 'good', label: '記得', cls: 'srs-act-good' },
  { key: 'easy', label: '容易', cls: 'srs-act-easy' },
];

// 複習 — ports the phone ReviewScreen: direction + scope picker → clean cards
// (word + 🔊 + 跳到故事) with the grade bar pinned to the bottom.
export default function SRSReview({ lib, onBack, onJump, resume, resumeWord, resumeDialect, onResumed }) {
  const { getLearningWords, getCard, updateCard, ready: vocabReady } = useVocab();
  const { getTimes } = useWordTimes();
  const [dia, setDia] = useState(undefined);       // undefined=choosing, null=all, else dialect key
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [direction, setDirection] = useState(() => localStorage.getItem('srs_direction') || 'fwd');  // fwd 賽→中 | rev 中→賽 | both
  useEffect(() => { localStorage.setItem('srs_direction', direction); }, [direction]);

  // Swipe up = reveal the answer (no left/right in review). Touch-only. Declared
  // before the early returns so the hook order stays stable across renders.
  const cardSwipe = useSwipe({ onUp: () => setShowAnswer(true) });

  // The word→dialect table, fetched when review opens. Until it lands, cards that
  // carry their own `dialect` still sort correctly (inDialect short-circuits on
  // it); only legacy cards without one need the table.
  const [wd, setWd] = useState(null);
  useEffect(() => { loadWordDialects(lib).then(setWd, () => setWd({})); }, [lib]);

  // All sixteen now, in the shelf's order, and named from the shelf's own table —
  // the review screen used to hold a private list of six.
  const DIA_ORDER = lib.review.order;
  const DIA_NAMES = lib.dialectNames;

  const inDia = (c, d) => d == null || inDialect(wd, c.word, c, d);
  const learningAll = getLearningWords();
  const dueAll = learningAll.filter(isDue);
  const byDia = useMemo(() => {
    const m = {};
    for (const d of DIA_ORDER) {
      const n = dueAll.filter(c => inDialect(wd, c.word, c, d)).length;
      if (n > 0) m[d] = n;
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningAll.length, dueAll.length, wd]);

  const current = dia !== undefined ? (queue[idx] || null) : null;
  const curCard = current ? getCard(current.dialect, current.word) : null;
  const dir = direction === 'both' ? (idx % 2 === 0 ? 'fwd' : 'rev') : direction;   // this card's front/back direction

  // Was a useMemo over the bundled TEXTS. The dialect's book is fetched now, so
  // the example arrives a tick after the card does — see the playback effects below.
  const [ex, setEx] = useState(null);
  useEffect(() => {
    if (!current) { setEx(null); return; }
    let live = true;
    setEx(null);
    loadStoriesIn(lib, current.dialect || dia)
      .then(sts => { if (live) setEx(findExample(sts, current.word, current.loc)); })
      .catch(() => { if (live) setEx(null); });   // no example beats a broken card
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, dia]);

  const leaf = ex ? ex.leaf : null;
  const keyB = ex ? `${ex.code}:${ex.leafId}` : null;            // SAME key the reader reads
  const origin = leaf ? (leaf.origin ?? 0) : 0;
  const eff = ex ? getTimes(keyB, ex.wi, ex.word) : null;        // effective time (override or default)
  const exUrl = leaf && (leaf.src || leaf.audio) ? `${MEDIA_BASE}/audio/${encPath(leaf.src || leaf.audio)}${MEDIA_V}` : null;
  const playW = () => { if (exUrl && eff) playWord(exUrl, origin + eff.start, origin + eff.end); };

  // 賽→中: play the word on the front (see + hear it). 中→賽: play it only on REVEAL —
  // after you've tried to produce it, so the front doesn't leak the pronunciation.
  // Keyed on `ex`, NOT on `current`. The example is fetched now, so on a card
  // change exUrl is still null for a tick; firing on `current` would find nothing
  // to play and never retry, and forward cards would silently stop speaking —
  // the card would look completely normal. Stopping stays on `current` so the
  // previous card's audio is cut the moment the card turns, fetch or no fetch.
  useEffect(() => () => stopAll(), [current]);
  useEffect(() => {
    if (dir === 'fwd' && exUrl && eff) playWord(exUrl, origin + eff.start, origin + eff.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ex]);
  useEffect(() => {
    if (dir === 'rev' && showAnswer && exUrl && eff) playWord(exUrl, origin + eff.start, origin + eff.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnswer, ex]);

  useEffect(() => {
    if (!current) { setDefinition(null); return; }
    let live = true;                                      // card can advance before the answer lands
    setDefinition(null); setShowAnswer(false);
    const d = current.dialect || dia;
    if (STATIC) {                                         // no backend: dict.js fetches the dialect from R2
      lookupGloss(current.word, d).then(g => { if (live) setDefinition({ gloss: g }); });
      return () => { live = false; };
    }
    const dq = d ? `?dialect=${encodeURIComponent(d)}` : '';
    fetch(`${API}/dictionary/${encodeURIComponent(cleanWord(current.word, d))}${dq}`)
      .then(r => r.json()).then(x => { if (live) setDefinition(x); })
      .catch(() => { if (live) setDefinition({ gloss: '' }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  function start(d) {
    setDia(d);
    setQueue(dueAll.filter(c => inDia(c, d)));
    setIdx(0); setShowAnswer(false);
  }

  // Returning from a 跳到故事 jump (or 跳到複習 from the reader): resume the session
  // AND land back on the same word, pre-reveal.
  useEffect(() => {
    if (!resume || !vocabReady) return;   // wait for vocab to load, else the queue is empty → menu
    const d = resumeDialect || null;
    const learning = getLearningWords().filter(c => inDia(c, d));
    let q = learning.filter(isDue);
    if (resumeWord && !q.some(c => c.word === resumeWord)) {
      const rw = learning.find(c => c.word === resumeWord);   // 跳到複習 a not-due word → drill it (pre-reveal)
      if (rw) q = [rw, ...q];
    }
    const i = resumeWord ? q.findIndex(c => c.word === resumeWord) : 0;
    setDia(d);
    setQueue(q);
    setIdx(i >= 0 ? i : 0);
    setShowAnswer(false);
    onResumed && onResumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, vocabReady]);

  function handleGrade(grade) {
    if (!current) return;
    stopAll();
    const patch = answer(getCard(current.dialect, current.word), grade);
    updateCard(current.dialect, current.word, { ...patch, status: 'learning' });
    const sameDay = patch.due.slice(0, 10) <= todayStr();
    if (sameDay) setQueue(q => [...q, current]);
    setIdx(i => i + 1);
  }

  // Choose scope: direction + all dialects or one (phone picker)
  if (dia === undefined) {
    if (dueAll.length === 0) {
      return (
        <div className="srs-review">
          <Header onBack={onBack} />
          <div className="srs-empty">
            <div className="srs-done-icon">✓</div>
            <div className="srs-done-txt">沒有到期的單詞</div>
            <p className="srs-done-sub">把單詞標為「學習中」就會排入複習；<br />記得的卡片會隔天、隔幾天再出現。</p>
            <button className="srs-btn" onClick={onBack}>回書庫</button>
          </div>
        </div>
      );
    }
    return (
      <div className="srs-review">
        <Header onBack={onBack} />
        <div className="srs-empty">
          <div className="srs-pick-title">選擇複習方向</div>
          <div className="srs-dir-row">
            {[['fwd', '賽→中'], ['rev', '中→賽'], ['both', '雙向']].map(([k, l]) => (
              <button key={k} className={`srs-dir-btn${direction === k ? ' on' : ''}`} onClick={() => setDirection(k)}>{l}</button>
            ))}
          </div>
          <div className="srs-pick-title" style={{ marginTop: 24 }}>選擇複習範圍</div>
          <button className="srs-pick-btn" onClick={() => start(null)}>
            <span className="srs-pick-txt">全部語類</span><span className="srs-pick-count">{dueAll.length}</span>
          </button>
          {Object.keys(byDia).map((d) => (
            <button key={d} className="srs-pick-btn" onClick={() => start(d)}>
              <span className="srs-pick-txt">{DIA_NAMES[d] || d}</span><span className="srs-pick-count">{byDia[d]}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Finished the chosen scope
  if (!current) {
    return (
      <div className="srs-review">
        <Header onBack={onBack} />
        <div className="srs-empty">
          <div className="srs-done-icon">✓</div>
          <div className="srs-done-txt">本輪複習完成</div>
          <button className="srs-btn" onClick={() => { setDia(undefined); setQueue([]); setIdx(0); }}>選擇其他語類</button>
          <button className="srs-btn srs-btn-alt" onClick={onBack}>回書庫</button>
        </div>
      </div>
    );
  }

  const gloss = definition && definition.gloss;
  const wordSide = (
    <>
      {/* cleanWord, not a \p{P} strip: it takes "da?" down to "da" like the strip
          did, and leaves ka’a’ alone instead of showing the card as ka’a. */}
      <div className="srs-word">{cleanWord(current.word, current.dialect || dia)}</div>
      {ex && <button className="srs-audio-btn" onClick={playW}>🔊　單詞</button>}
      {onJump && ex && (
        <button className="srs-jump-btn"
          onClick={() => onJump(ex.st, ex.leafId, current.dialect || dia, current.word, ex.wi, ex.s.id)}
          title="跳到閱讀器，回到標記這個詞的地方">跳到故事 ↗</button>
      )}
    </>
  );
  // A flashcard answer has to be one thing you can hold in your head, and the
  // e-dict entries are aggregates of every sense (see utils/glossText). Show the
  // first few and say how many are left — the popup in the reader expands them.
  const folded = shortGloss(gloss, 3);
  const glossSide = (
    <div className="srs-word srs-word-zh">
      {definition === null ? '…' : (folded.head || '（查無解釋）')}
      {!!folded.more && <span className="srs-more-senses">　＋{folded.more}</span>}
    </div>
  );

  return (
    <div className="srs-review">
      <Header onBack={onBack} count={`剩 ${Math.max(0, queue.length - idx)}`} />
      <div className="srs-body" {...cardSwipe}>
        <div className="srs-dialect">{DIA_NAMES[current.dialect] || current.dialect}</div>
        {dir === 'fwd' ? (
          <>
            {wordSide}
            {showAnswer && <div className="srs-reveal">{glossSide}</div>}
          </>
        ) : (
          <>
            {glossSide}
            {showAnswer && <div className="srs-reveal">{wordSide}</div>}
          </>
        )}
      </div>

      {showAnswer ? (
        <div className="srs-actions">
          {GRADES.map((g) => (
            <button key={g.key} className={`srs-act ${g.cls}`} onClick={() => handleGrade(g.key)}>
              {g.label}
              <span className="srs-ivl">{previewLabel(curCard, g.key)}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="srs-actions">
          <button className="srs-act srs-act-show" onClick={() => setShowAnswer(true)}>
            {dir === 'rev' ? '顯示族語' : '顯示答案'}
          </button>
        </div>
      )}
    </div>
  );
}
